/**
 * /design-probe — THROWAWAY route for Commit 1 of the Wolf DMS C4 reskin.
 *
 * Purpose: verify that the additive tokens in `styles/globals.css` produce
 * working Tailwind utilities BEFORE the shadcn alias commit (Commit 2)
 * touches 376+ utility bindings across the app.
 *
 * Probe order (intentional):
 *   1. Class resolution — does the build generate the multi-segment utilities?
 *   2. Canvas-context probe — does `bg-card-bg backdrop-blur-sm` read as
 *      navy-tinted glass over the dot grid in `.dark`?
 *   3. Accent-color probe — does `accent-brand-600` apply to native radio?
 *
 * Keep available as a token-resolution regression check; delete once the
 * navy palette has stabilized across all real screens (after Commit 3+).
 */

import { ThemeToggle } from "@/components/layout/ThemeToggle";

export function DesignProbePage() {
  const dotGrid: React.CSSProperties = {
    backgroundImage: "radial-gradient(circle at 1px 1px, var(--canvas-dot) 1px, transparent 0)",
    backgroundSize: "24px 24px",
  };

  return (
    <div className="min-h-screen bg-surface text-ink">
      <header className="flex h-header items-center justify-between px-6 border-b border-border">
        <div>
          <div className="text-eyebrow uppercase tracking-eyebrow text-ink-subtle">
            Commit 1 probe
          </div>
          <div className="text-h-mono font-bold text-ink-heading tracking-tighter">
            Wolf DMS C4 — token resolution
          </div>
        </div>
        <ThemeToggle />
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10 space-y-12">
        {/* ───── Stage A: class resolution ───── */}
        <section>
          <h2 className="text-eyebrow uppercase tracking-eyebrow text-ink-subtle mb-3">
            Stage A — Class resolution
          </h2>
          <p className="text-body text-ink-muted mb-4">
            Each swatch is a multi-segment utility. If any cell renders with no background color or
            unstyled text, the build isn't generating that class — stop and investigate before
            continuing.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Swatch className="bg-navy-800 text-ink-on-inverse" label="bg-navy-800" />
            <Swatch className="bg-navy-900 text-ink-on-inverse" label="bg-navy-900" />
            <Swatch className="bg-brand-600 text-ink-on-inverse" label="bg-brand-600" />
            <Swatch className="bg-brand-700 text-ink-on-inverse" label="bg-brand-700" />
            <Swatch className="bg-brand-50 text-brand-600" label="bg-brand-50" />
            <Swatch className="bg-surface text-ink" label="bg-surface" />
            <Swatch className="bg-surface-elevated text-ink" label="bg-surface-elevated" />
            <Swatch className="bg-surface-tint text-ink" label="bg-surface-tint" />
            <Swatch
              className="bg-success-soft text-success-ink"
              label="bg-success-soft + text-success-ink"
            />
            <Swatch
              className="bg-warning-soft text-warning-ink"
              label="bg-warning-soft + text-warning-ink"
            />
            <Swatch
              className="bg-danger-soft text-danger-ink"
              label="bg-danger-soft + text-danger-ink"
            />
            <Swatch className="bg-info-soft text-info-ink" label="bg-info-soft + text-info-ink" />
            <Swatch className="bg-risk-cost text-ink-on-inverse" label="bg-risk-cost" />
            <Swatch
              className="bg-risk-performance text-ink-on-inverse"
              label="bg-risk-performance"
            />
            <Swatch className="bg-risk-security text-ink-on-inverse" label="bg-risk-security" />
            <Swatch className="bg-risk-compliance text-ink-on-inverse" label="bg-risk-compliance" />
          </div>

          <div className="mt-6 space-y-1">
            <p className="text-2xs text-ink-subtle">text-2xs · 9px / 1.2</p>
            <p className="text-eyebrow uppercase tracking-eyebrow text-ink-subtle">
              text-eyebrow · 10px tracked +0.08em
            </p>
            <p className="text-kbd text-ink-subtle">text-kbd · 11px / 1</p>
            <p className="text-meta text-ink-muted">text-meta · 11.5px / 1.3</p>
            <p className="text-caption text-ink-muted">text-caption · 12px / 1.4</p>
            <p className="text-ui text-ink">text-ui · 13px / 1.5 (workhorse)</p>
            <p className="text-body text-ink-muted">text-body · 13.5px / 1.55</p>
            <p className="text-h-mono font-bold text-ink-heading tracking-tighter">
              text-h-mono · 18px / 1.2
            </p>
            <p className="text-h-panel font-bold text-ink-heading tracking-tighter">
              text-h-panel · 22px / 1.2
            </p>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Box className="shadow-xs bg-surface-elevated rounded-md p-3 text-ui">shadow-xs</Box>
            <Box className="shadow-sm bg-surface-elevated rounded-md p-3 text-ui">shadow-sm</Box>
            <Box className="shadow-md bg-surface-elevated rounded-md p-3 text-ui">shadow-md</Box>
            <Box className="shadow-lg bg-surface-elevated rounded-md p-3 text-ui">shadow-lg</Box>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="rounded-md bg-navy-800 text-ink-on-inverse px-3.5 py-2.5 text-ui font-semibold tracking-tight transition-all duration-base ease-out-soft hover:bg-brand-600 hover:-translate-y-px hover:shadow-cta-hover focus-visible:outline-none focus-visible:shadow-ring-brand-strong"
            >
              Tab here → focus ring (shadow-ring-brand-strong)
            </button>
            <input
              type="text"
              placeholder="Focus me → shadow-ring-brand"
              className="rounded-md border border-border bg-surface px-3 py-2 text-ui text-ink placeholder:text-ink-subtle outline-none focus:border-brand-600 focus:shadow-ring-brand transition-[border-color,box-shadow] duration-base ease-out-soft"
            />
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="rounded-pill bg-surface-tint text-ink-muted px-3 py-1 text-kbd">
              rounded-pill
            </span>
            <span className="rounded-xs bg-warning-soft text-warning-ink px-1.5 py-0.5 text-2xs uppercase tracking-eyebrow font-bold">
              rounded-xs
            </span>
            <span className="rounded-2xl bg-brand-50 text-brand-600 px-3 py-1 text-meta font-semibold">
              rounded-2xl
            </span>
            <span className="rounded-3xl bg-surface-tint text-ink-muted px-3 py-1 text-meta">
              rounded-3xl
            </span>
          </div>
        </section>

        {/* ───── Stage B: canvas-context probe ───── */}
        <section>
          <h2 className="text-eyebrow uppercase tracking-eyebrow text-ink-subtle mb-3">
            Stage B — Canvas-context glass
          </h2>
          <p className="text-body text-ink-muted mb-4">
            The card below uses{" "}
            <code className="font-mono text-caption bg-surface-tint border border-border rounded-xs px-1.5">
              bg-card-bg backdrop-blur-sm
            </code>{" "}
            over{" "}
            <code className="font-mono text-caption bg-surface-tint border border-border rounded-xs px-1.5">
              bg-surface-canvas
            </code>{" "}
            with a 24px dot grid. In dark mode it must read as <strong>navy-tinted glass</strong>,
            not muddy charcoal.
          </p>

          <div
            className="relative bg-surface-canvas border border-border rounded-lg p-12 overflow-hidden"
            style={dotGrid}
          >
            {/* Floating dock card (cluster of three to show stacking + spacing) */}
            <div className="absolute left-4 bottom-4 flex flex-col gap-2 max-w-[240px]">
              <ProbeCard title="Legend">
                <LegendRow swatch="bg-navy-800" label="System" />
                <LegendRow swatch="bg-brand-600" label="Component" />
                <LegendRow swatch="bg-surface-tint" label="External" />
              </ProbeCard>
              <ProbeCard title="Flows">
                <p className="text-meta text-ink-muted">Read list · Upload · SP change</p>
              </ProbeCard>
              <ProbeCard title="Risk filter">
                <div className="flex flex-wrap gap-1.5">
                  <RiskChip kind="cost">cost</RiskChip>
                  <RiskChip kind="performance">perf</RiskChip>
                  <RiskChip kind="security">sec</RiskChip>
                  <RiskChip kind="compliance">comp</RiskChip>
                </div>
              </ProbeCard>
            </div>

            {/* Zoom controls — bottom right */}
            <div className="absolute right-4 bottom-4 bg-card-bg backdrop-blur-sm border border-border rounded-xl shadow-sm p-1 flex flex-col gap-1">
              <ZoomBtn>+</ZoomBtn>
              <ZoomBtn>−</ZoomBtn>
              <ZoomBtn>0</ZoomBtn>
            </div>

            {/* Banner — bottom center */}
            <div className="absolute left-1/2 -translate-x-1/2 bottom-4 bg-navy-800 text-ink-on-inverse px-4 py-2 rounded-pill text-caption max-w-[70%] shadow-md">
              Flow step 2 of 5 — packet leaves the API
            </div>

            <p className="text-h-mono font-bold text-ink-heading tracking-tighter">
              Canvas surface
            </p>
            <p className="text-body text-ink-muted">
              The dot grid uses{" "}
              <code className="font-mono text-caption bg-surface-tint border border-border rounded-xs px-1.5">
                var(--canvas-dot)
              </code>
              . Glass cards should read as a clearly-different translucent layer over this surface.
            </p>
          </div>
        </section>

        {/* ───── Stage C: accent-color probe ───── */}
        <section>
          <h2 className="text-eyebrow uppercase tracking-eyebrow text-ink-subtle mb-3">
            Stage C — Native input accent-color
          </h2>
          <p className="text-body text-ink-muted mb-4">
            Both inputs should render their checked state in{" "}
            <code className="font-mono text-caption bg-surface-tint border border-border rounded-xs px-1.5">
              --brand-600
            </code>
            . The first uses the Tailwind utility; the second uses an inline fallback in case
            <code className="font-mono text-caption bg-surface-tint border border-border rounded-xs px-1.5">
              accent-brand-600
            </code>{" "}
            doesn't compile.
          </p>

          <div className="grid grid-cols-2 gap-6 max-w-md">
            <fieldset className="border border-border rounded-lg p-4 bg-surface-elevated">
              <legend className="text-meta font-semibold text-ink-muted px-2">
                accent-brand-600 (utility)
              </legend>
              <label className="flex items-center gap-2 py-1 cursor-pointer">
                <input type="radio" name="probe-a" defaultChecked className="accent-brand-600" />
                <span className="text-meta text-ink">Option A</span>
              </label>
              <label className="flex items-center gap-2 py-1 cursor-pointer">
                <input type="radio" name="probe-a" className="accent-brand-600" />
                <span className="text-meta text-ink-muted">Option B</span>
              </label>
              <label className="flex items-center gap-2 py-1 cursor-pointer">
                <input type="checkbox" defaultChecked className="accent-brand-600" />
                <span className="text-meta text-ink">Checkbox</span>
              </label>
            </fieldset>

            <fieldset className="border border-border rounded-lg p-4 bg-surface-elevated">
              <legend className="text-meta font-semibold text-ink-muted px-2">
                Inline fallback
              </legend>
              <label className="flex items-center gap-2 py-1 cursor-pointer">
                <input
                  type="radio"
                  name="probe-b"
                  defaultChecked
                  style={{ accentColor: "var(--brand-600)" }}
                />
                <span className="text-meta text-ink">Option A</span>
              </label>
              <label className="flex items-center gap-2 py-1 cursor-pointer">
                <input type="radio" name="probe-b" style={{ accentColor: "var(--brand-600)" }} />
                <span className="text-meta text-ink-muted">Option B</span>
              </label>
              <label className="flex items-center gap-2 py-1 cursor-pointer">
                <input type="checkbox" defaultChecked style={{ accentColor: "var(--brand-600)" }} />
                <span className="text-meta text-ink">Checkbox</span>
              </label>
            </fieldset>
          </div>
        </section>

        <footer className="border-t border-border pt-6 text-meta text-ink-subtle">
          If everything reads correctly in both themes, give the go-ahead for Commit 2 (shadcn alias
          swap + AuthLayout gradient override).
        </footer>
      </main>
    </div>
  );
}

function Swatch({ className, label }: { className: string; label: string }) {
  return (
    <div className={`${className} rounded-md px-3 py-3 text-meta font-mono shadow-xs`}>{label}</div>
  );
}

function Box({ className, children }: { className: string; children: React.ReactNode }) {
  return <div className={className}>{children}</div>;
}

function ProbeCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-card-bg backdrop-blur-sm border border-border rounded-xl shadow-sm px-2.5 py-2.5">
      <div className="text-eyebrow font-bold text-ink-subtle uppercase tracking-eyebrow mb-1.5">
        {title}
      </div>
      {children}
    </div>
  );
}

function LegendRow({ swatch, label }: { swatch: string; label: string }) {
  return (
    <div className="flex items-center gap-2 py-0.5">
      <span className={`${swatch} w-3 h-3 rounded-xs border border-border`} />
      <span className="text-meta text-ink-muted">{label}</span>
    </div>
  );
}

function RiskChip({
  kind,
  children,
}: {
  kind: "cost" | "performance" | "security" | "compliance";
  children: React.ReactNode;
}) {
  const styles: Record<string, string> = {
    cost: "bg-warning/15 text-warning-ink",
    performance: "bg-brand-600/12 text-brand-600",
    security: "bg-danger/12 text-danger",
    compliance: "bg-risk-compliance/12 text-risk-compliance",
  };
  return (
    <span
      className={`${styles[kind]} font-mono text-2xs font-bold uppercase tracking-eyebrow px-1.5 py-0.5 rounded-xs text-center`}
    >
      {children}
    </span>
  );
}

function ZoomBtn({ children }: { children: React.ReactNode }) {
  return (
    <button
      type="button"
      className="w-8 h-8 inline-grid place-items-center bg-transparent border-0 rounded-md text-ink-muted hover:bg-surface-tint hover:text-ink-heading transition-colors duration-base ease-out-soft text-ui"
    >
      {children}
    </button>
  );
}
